<template>
  <div class="filter-bar" :class="{ 'filter-bar--collapsible': mobileCollapsible, 'filter-bar--open': mobileOpen }">
    <div class="filter-bar__row" ref="rowEl">
      <!-- Filter chips -->
      <div class="filter-bar__chips" ref="chipsEl">
        <!-- Saved views chip (opt-in via saved-filters-key). Always first; never overflows. -->
        <el-popover
          v-if="saved"
          trigger="click"
          placement="bottom-start"
          popper-class="fb-popover"
          :value="viewsOpen"
          @input="(v) => (viewsOpen = v)"
        >
          <div class="fb-views">
            <div class="fb-pop__header">Saved views</div>
            <div class="fb-views__list">
              <div
                v-for="p in savedPresets"
                :key="'fbv-' + p.id"
                class="fb-views__item"
                :class="{ 'fb-views__item--active': appliedPresetId === p.id }"
              >
                <span class="fb-views__name" @click="applyPreset(p)">{{ p.name }}</span>
                <button
                  type="button"
                  class="fb-views__star"
                  :class="{ 'fb-views__star--on': p.is_default }"
                  :title="p.is_default ? 'Default view' : 'Set as default'"
                  @click.stop="toggleDefault(p)"
                >
                  <i class="tim-icons icon-shape-star"></i>
                </button>
                <button type="button" class="fb-views__del" title="Delete view" @click.stop="onDeletePreset(p)">
                  <i class="tim-icons icon-trash-simple"></i>
                </button>
              </div>
              <div v-if="savedPresets.length === 0" class="fb-ms-empty">No saved views yet</div>
            </div>
            <div class="fb-views__save">
              <input
                v-model="newViewName"
                type="text"
                class="fb-input"
                placeholder="Save current as..."
                @keyup.enter="onSavePreset"
              />
              <button type="button" class="fb-apply fb-views__save-btn" :disabled="!newViewName.trim() || savingPreset" @click="onSavePreset">Save</button>
            </div>
            <button type="button" class="fb-views__manage" @click="onManageViews">
              <i class="tim-icons icon-settings-gear-63"></i>
              <span>Manage views</span>
            </button>
          </div>
          <button slot="reference" type="button" class="fb-chip fb-chip--views" data-fb-views :class="{ 'fb-chip--active': appliedPresetId != null }">
            <span class="fb-chip__icon"><i class="tim-icons icon-book-bookmark"></i></span>
            <span class="fb-chip__label">{{ currentViewLabel }}</span>
            <i class="tim-icons icon-minimal-down fb-chip__chevron"></i>
          </button>
        </el-popover>

        <template v-for="def in visibleChips">
          <el-popover
            :key="'fb-' + def.key"
            trigger="click"
            placement="bottom-start"
            popper-class="fb-popover"
            :value="openKey === def.key"
            @input="(v) => onPopoverToggle(def, v)"
          >
            <!-- Popover body -->
            <div class="fb-pop">
              <div class="fb-pop__header">Filter by: {{ def.label.toLowerCase() }}</div>

              <!-- Text -->
              <div v-if="def.type === 'text'" class="fb-pop__body">
                <div class="fb-pop__op">{{ def.operatorLabel || 'contains' }}</div>
                <input
                  type="text"
                  class="fb-input"
                  :placeholder="def.placeholder || def.label"
                  :value="draft[def.key]"
                  @input="draft[def.key] = $event.target.value"
                  @keyup.enter="applyFilter()"
                />
              </div>

              <!-- Select -->
              <div v-else-if="def.type === 'select'" class="fb-pop__body">
                <input
                  v-if="(def.options || []).length > 8"
                  type="text"
                  class="fb-input fb-ms-search"
                  placeholder="Search"
                  :value="msQuery[def.key]"
                  @input="msQuery[def.key] = $event.target.value"
                />
                <div class="fb-ms-list">
                  <template v-for="(opt, i) in filteredOptions(def)">
                    <div
                      v-if="opt.group && (i === 0 || filteredOptions(def)[i - 1].group !== opt.group)"
                      :key="'fbog-' + def.key + '-' + i"
                      class="fb-ms-group"
                    >{{ opt.group }}</div>
                    <div
                      :key="'fbo-' + def.key + '-' + opt.value"
                      class="fb-option"
                      @click="draft[def.key] = draft[def.key] === opt.value ? '' : opt.value"
                    >
                      <span class="fb-option__radio" :class="{ 'fb-option__radio--on': draft[def.key] === opt.value }">
                        <span v-if="draft[def.key] === opt.value" class="fb-option__dot"></span>
                      </span>
                      <span class="fb-option__label">{{ opt.label }}</span>
                      <span v-if="opt.badge" class="badge-tag badge-tag--archived ml-2">{{ opt.badge }}</span>
                    </div>
                  </template>
                  <div v-if="filteredOptions(def).length === 0" class="fb-ms-empty">No matches</div>
                </div>
              </div>

              <!-- Select + text (mutually exclusive modes; one mode reveals a text input) -->
              <div v-else-if="def.type === 'selecttext'" class="fb-pop__body">
                <div
                  v-for="opt in def.options"
                  :key="'fbst-' + def.key + '-' + opt.value"
                  class="fb-option"
                  @click="draft[def.key].mode = draft[def.key].mode === opt.value ? '' : opt.value"
                >
                  <span class="fb-option__radio" :class="{ 'fb-option__radio--on': draft[def.key].mode === opt.value }">
                    <span v-if="draft[def.key].mode === opt.value" class="fb-option__dot"></span>
                  </span>
                  <span class="fb-option__label">{{ opt.label }}</span>
                </div>
                <input
                  v-if="draft[def.key].mode === def.textOption"
                  type="text"
                  class="fb-input fb-selecttext__input"
                  :placeholder="def.placeholder || def.label"
                  :value="draft[def.key].text"
                  @input="draft[def.key].text = $event.target.value"
                  @keyup.enter="applyFilter()"
                />
              </div>

              <!-- Toggle -->
              <div v-else-if="def.type === 'toggle'" class="fb-pop__body">
                <div class="fb-pop__toggle">
                  <span>{{ def.label }}</span>
                  <el-switch active-color="#e14eca" v-model="draft[def.key]" />
                </div>
              </div>

              <!-- Multi-select -->
              <div v-else-if="def.type === 'multiselect'" class="fb-pop__body">
                <input
                  v-if="(def.options || []).length > 8"
                  type="text"
                  class="fb-input fb-ms-search"
                  placeholder="Search"
                  :value="msQuery[def.key]"
                  @input="msQuery[def.key] = $event.target.value"
                />
                <div v-if="def.presets && def.presets.length > 0" class="fb-ms-presets">
                  <dropdown trigger="click" @command="(p) => applyOptionPreset(def, p)">
                    <base-button variant="outlined" tone="primary" size="sm"><i class="tim-icons icon-pin"></i>{{ def.presetsLabel || 'Select by' }}</base-button>
                    <template #dropdown>
                      <dropdown-menu>
                        <dropdown-item v-for="p in def.presets" :key="'fbp-' + def.key + '-' + p.key" :command="p" :divided="!!p.divided">{{ p.label }}</dropdown-item>
                      </dropdown-menu>
                    </template>
                  </dropdown>
                </div>
                <div class="fb-ms-list">
                  <div
                    v-if="def.selectAll && filteredOptions(def).length > 0"
                    class="fb-option fb-option--all"
                    @click="toggleBulk(def, filteredOptions(def).map(o => o.value))"
                  >
                    <span class="fb-check" :class="{ 'fb-check--on': bulkState(def, filteredOptions(def).map(o => o.value)) === 'on', 'fb-check--mixed': bulkState(def, filteredOptions(def).map(o => o.value)) === 'mixed' }">
                      <i v-if="bulkState(def, filteredOptions(def).map(o => o.value)) === 'on'" class="tim-icons icon-check-2"></i>
                      <span v-else-if="bulkState(def, filteredOptions(def).map(o => o.value)) === 'mixed'" class="fb-check__bar"></span>
                    </span>
                    <span class="fb-option__label">{{ def.selectAllLabel || 'All' }}</span>
                  </div>
                  <template v-for="(opt, i) in filteredOptions(def)">
                    <div
                      v-if="opt.group && (i === 0 || filteredOptions(def)[i - 1].group !== opt.group)"
                      :key="'fbmsg-' + def.key + '-' + i"
                      class="fb-ms-group"
                      :class="{ 'fb-ms-group--select': def.selectAll }"
                      @click="def.selectAll && toggleBulk(def, groupValues(def, opt.group))"
                    >
                      <span v-if="def.selectAll" class="fb-check fb-check--sm" :class="{ 'fb-check--on': bulkState(def, groupValues(def, opt.group)) === 'on', 'fb-check--mixed': bulkState(def, groupValues(def, opt.group)) === 'mixed' }">
                        <i v-if="bulkState(def, groupValues(def, opt.group)) === 'on'" class="tim-icons icon-check-2"></i>
                        <span v-else-if="bulkState(def, groupValues(def, opt.group)) === 'mixed'" class="fb-check__bar"></span>
                      </span>
                      <span>{{ opt.group }}</span>
                    </div>
                    <div
                      :key="'fbms-' + def.key + '-' + i + '-' + opt.value"
                      class="fb-option"
                      @click="toggleMulti(def, opt.value)"
                    >
                      <span class="fb-check" :class="{ 'fb-check--on': isChecked(def, opt.value) }">
                        <i v-if="isChecked(def, opt.value)" class="tim-icons icon-check-2"></i>
                      </span>
                      <span class="fb-option__label">{{ opt.label }}</span>
                      <span v-if="opt.badge" class="badge-tag badge-tag--archived ml-2">{{ opt.badge }}</span>
                    </div>
                  </template>
                  <div v-if="filteredOptions(def).length === 0" class="fb-ms-empty">No matches</div>
                </div>
              </div>

              <!-- Number range -->
              <div v-else-if="def.type === 'numberrange'" class="fb-pop__body">
                <div class="fb-range-row">
                  <input
                    type="number"
                    class="fb-input"
                    placeholder="From"
                    :disabled="def.allowUnlimited && draft[def.key].unlimited"
                    :value="draft[def.key].from"
                    @input="draft[def.key].from = $event.target.value"
                  />
                  <span class="fb-range-dash">–</span>
                  <input
                    type="number"
                    class="fb-input"
                    placeholder="To"
                    :disabled="def.allowUnlimited && draft[def.key].unlimited"
                    :value="draft[def.key].to"
                    @input="draft[def.key].to = $event.target.value"
                    @keyup.enter="applyFilter()"
                  />
                </div>
                <label v-if="def.allowUnlimited" class="fb-radio mt-2">
                  <input type="checkbox" :checked="draft[def.key].unlimited" @change="draft[def.key].unlimited = $event.target.checked" />
                  <span>Unlimited</span>
                </label>
              </div>

              <!-- Time range -->
              <div v-else-if="def.type === 'timerange'" class="fb-pop__body">
                <div class="fb-date-range-row">
                  <time-picker
                    :editable="false"
                    placeholder="From"
                    value-format="HH:mm"
                    :format="timeDisplayFormat"
                    :value="draft[def.key].from"
                    @input="draft[def.key].from = $event || ''"
                  ></time-picker>
                  <time-picker
                    :editable="false"
                    placeholder="To"
                    value-format="HH:mm"
                    :format="timeDisplayFormat"
                    :value="draft[def.key].to"
                    @input="draft[def.key].to = $event || ''"
                  ></time-picker>
                </div>
              </div>

              <!-- Date range. Rendered for daterange defs, and inside a select popover whose
                   chosen option (`rangeOption`) reveals the companion daterange (`rangeKey`). -->
              <div v-if="rangeDraftKey(def)" class="fb-pop__body" :class="{ 'fb-pop__body--nested': def.type !== 'daterange' }">
                <label class="fb-radio">
                  <input type="radio" :checked="draft[rangeDraftKey(def)].mode === 'days'" @change="draft[rangeDraftKey(def)].mode = 'days'" />
                  <span>Within the last</span>
                </label>
                <div class="fb-date-days-row">
                  <input
                    type="number"
                    min="1"
                    class="fb-input fb-input--days"
                    :value="draft[rangeDraftKey(def)].days"
                    @focus="draft[rangeDraftKey(def)].mode = 'days'"
                    @input="draft[rangeDraftKey(def)].days = $event.target.value"
                  />
                  <span class="fb-date-suffix">days</span>
                </div>
                <label class="fb-radio mt-2">
                  <input type="radio" :checked="draft[rangeDraftKey(def)].mode === 'range'" @change="draft[rangeDraftKey(def)].mode = 'range'" />
                  <span>Date range</span>
                </label>
                <div class="fb-date-range-row">
                  <el-date-picker
                    :editable="false"
                    type="date"
                    placeholder="From"
                    value-format="yyyy-MM-dd"
                    :format="dateDisplayFormat"
                    :value="draft[rangeDraftKey(def)].from"
                    :picker-options="{ firstDayOfWeek }"
                    @focus="draft[rangeDraftKey(def)].mode = 'range'"
                    @input="draft[rangeDraftKey(def)].from = $event || ''"
                  ></el-date-picker>
                  <el-date-picker
                    :editable="false"
                    type="date"
                    placeholder="To"
                    value-format="yyyy-MM-dd"
                    :format="dateDisplayFormat"
                    :value="draft[rangeDraftKey(def)].to"
                    :picker-options="{ firstDayOfWeek }"
                    @focus="draft[rangeDraftKey(def)].mode = 'range'"
                    @input="draft[rangeDraftKey(def)].to = $event || ''"
                  ></el-date-picker>
                </div>
              </div>

              <button type="button" class="fb-apply" @click="applyFilter()">Apply</button>
            </div>

            <!-- Chip (reference) -->
            <button
              slot="reference"
              type="button"
              class="fb-chip"
              data-fb-chip
              :class="{ 'fb-chip--active': isActive(def) }"
            >
              <span class="fb-chip__icon" @click="onIconClick(def, $event)">
                <i v-if="isActive(def)" class="tim-icons icon-simple-remove"></i>
                <i v-else class="tim-icons icon-simple-add"></i>
              </span>
              <span class="fb-chip__label">{{ def.label }}</span>
              <template v-if="isActive(def)">
                <span class="fb-chip__sep">|</span>
                <span class="fb-chip__value">{{ valueSummary(def) }}</span>
                <i class="tim-icons icon-minimal-down fb-chip__chevron"></i>
              </template>
            </button>
          </el-popover>
        </template>

        <!-- Quick-action chips (e.g. "Today's bookings") — self-contained toggles styled
             like filter chips. Parent owns the active state and emits drive it.
             `end: true` renders a chip after the "More filters" chip instead of before;
             `solid: true` gives it a solid outline. -->
        <button
          v-for="qa in quickActionsBefore"
          :key="'fb-qa-' + qa.key"
          type="button"
          class="fb-chip"
          data-fb-qa
          :class="{ 'fb-chip--active': qa.active, 'fb-chip--solid': qa.solid }"
          @click="$emit('quick-action', qa.key)"
        >
          <span v-if="qa.active" class="fb-chip__icon"><i class="tim-icons icon-simple-remove"></i></span>
          <span class="fb-chip__label">{{ qa.label }}</span>
        </button>

        <!-- Sort chip (opt-in via sort-options). Field list plus a direction choice;
             each click applies immediately and emits update:sort. Persisted in saved
             views as _sort. -->
        <el-popover
          v-if="sortOptions.length > 0 && showSortChip"
          trigger="click"
          placement="bottom-start"
          popper-class="fb-popover"
          :value="sortOpen"
          @input="(v) => (sortOpen = v)"
        >
          <div class="fb-pop">
            <div class="fb-pop__header">Sort by</div>
            <div class="fb-pop__body">
              <div class="fb-ms-list">
                <div
                  v-for="opt in sortOptions"
                  :key="'fbs-' + opt.key"
                  class="fb-option"
                  @click="setSort(opt.key, currentSort.dir)"
                >
                  <span class="fb-option__radio" :class="{ 'fb-option__radio--on': currentSort.key === opt.key }">
                    <span v-if="currentSort.key === opt.key" class="fb-option__dot"></span>
                  </span>
                  <span class="fb-option__label">{{ opt.label }}</span>
                </div>
              </div>
              <div class="fb-pop__op fb-sort__dir-label">Direction</div>
              <div class="fb-ms-list">
                <div class="fb-option" @click="setSort(currentSort.key, 'asc')">
                  <span class="fb-option__radio" :class="{ 'fb-option__radio--on': currentSort.dir === 'asc' }">
                    <span v-if="currentSort.dir === 'asc'" class="fb-option__dot"></span>
                  </span>
                  <span class="fb-option__label">{{ sortDirLabels.asc }}</span>
                </div>
                <div class="fb-option" @click="setSort(currentSort.key, 'desc')">
                  <span class="fb-option__radio" :class="{ 'fb-option__radio--on': currentSort.dir === 'desc' }">
                    <span v-if="currentSort.dir === 'desc'" class="fb-option__dot"></span>
                  </span>
                  <span class="fb-option__label">{{ sortDirLabels.desc }}</span>
                </div>
              </div>
            </div>
          </div>
          <button slot="reference" type="button" class="fb-chip fb-chip--active" data-fb-sort>
            <span class="fb-chip__icon"><i class="tim-icons icon-minimal-down fb-sort__flip" :class="{ 'fb-sort__flip--asc': currentSort.dir === 'asc' }" @click.stop="flipSort"></i></span>
            <span class="fb-chip__label">Sort</span>
            <span class="fb-chip__sep">|</span>
            <span class="fb-chip__value">{{ currentSortLabel }}</span>
            <i class="tim-icons icon-minimal-down fb-chip__chevron"></i>
          </button>
        </el-popover>

        <!-- More filters -->
        <el-popover
          v-if="moreFilters.length > 0"
          trigger="click"
          placement="bottom-start"
          popper-class="fb-popover fb-popover--menu"
          :value="moreOpen"
          @input="(v) => (moreOpen = v)"
        >
          <div class="fb-more">
            <button
              v-for="def in moreFilters"
              :key="'fbm-' + def.key"
              type="button"
              class="fb-more__item"
              @click="addFromMore(def)"
            >
              <i class="tim-icons icon-simple-add"></i>
              <span>{{ def.label }}</span>
            </button>
          </div>
          <button slot="reference" type="button" class="fb-chip fb-chip--more" data-fb-more>
            <span class="fb-chip__icon"><i class="tim-icons icon-simple-add"></i></span>
            <span class="fb-chip__label">More filters</span>
          </button>
        </el-popover>

        <!-- Quick-action chips flagged end: true land after "More filters" -->
        <button
          v-for="qa in quickActionsAfter"
          :key="'fb-qa-' + qa.key"
          type="button"
          class="fb-chip"
          data-fb-qa
          :class="{ 'fb-chip--active': qa.active, 'fb-chip--solid': qa.solid }"
          @click="$emit('quick-action', qa.key)"
        >
          <span v-if="qa.active" class="fb-chip__icon"><i class="tim-icons icon-simple-remove"></i></span>
          <span class="fb-chip__label">{{ qa.label }}</span>
        </button>

        <!-- Clear filters (visible whenever the page has filters; dimmed when nothing to clear) -->
        <button
          v-if="filters.length > 0"
          type="button"
          class="fb-clear"
          data-fb-clear
          :class="{ 'fb-clear--disabled': !hasActiveFilters }"
          :disabled="!hasActiveFilters"
          @click="clearAll"
        >Clear filters</button>
      </div>

      <!-- Right cluster: only when it has something to show (an empty one still took a 30px line
           of its own on phones, leaving dead space under the filters) -->
      <div v-if="resultCount != null || perPage != null || $slots.actions || mobileCollapsible" class="filter-bar__right" ref="rightEl">
        <span v-if="resultCount != null" class="filter-bar__count">
          <i v-if="loading" class="el-icon-loading filter-bar__spinner"></i>
          <!-- `count` slot: custom count copy ("10 checklists · 9 in progress"); default is "N results" -->
          <slot name="count" :count="resultCount">{{ resultCount }} result{{ resultCount !== 1 ? 's' : '' }}</slot>
        </span>
        <slot name="actions"></slot>
        <el-select
          v-if="perPage != null"
          class="select-primary pagination-select filter-bar__perpage"
          size="small"
          :value="perPage"
          placeholder="Per page"
          @change="$emit('update:perPage', $event)"
        >
          <el-option
            v-for="option in perPageOptions"
            :key="'fb-pp-' + option"
            class="select-primary"
            :label="option.toString()"
            :value="option"
          ></el-option>
        </el-select>
        <!-- Phones only (mobileCollapsible): the chip row hides behind this button, the same
             square filter button with an active-count badge the bookings mobile toolbar uses. -->
        <button
          v-if="mobileCollapsible"
          type="button"
          class="filter-bar__toggle"
          :class="{ 'is-open': mobileOpen }"
          :aria-expanded="mobileOpen ? 'true' : 'false'"
          aria-label="Filters"
          @click="toggleMobile"
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" width="18" height="18">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M7 12h10M10 18h4" />
          </svg>
          <span v-if="activeCount > 0" class="filter-bar__toggle-badge">{{ activeCount }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { reactive, ref, computed, nextTick, watch, set, onMounted, onBeforeUnmount, getCurrentInstance } from 'vue'
// Element's dropdown parts and time picker aren't globally registered. Import unaliased:
// the babel-plugin-component resolver derives the lib path from the binding name.
// Used in the template as <dropdown> / <dropdown-menu> / <dropdown-item> / <time-picker>.
import { Dropdown, DropdownMenu, DropdownItem, TimePicker } from 'element-ui'
import { diacriticIncludes } from '@/util/search'
import { useSavedFilters } from '@/composables/useSavedFilters'
import { openSavedViews } from '@/composables/useSavedViewsDrawer'
import type { SavedFilterRow } from '@/util/savedFiltersApi'

// `group`: optional heading; consecutive options sharing a group render under one header row.
interface FilterOption { label: string; value: string | number; group?: string; badge?: string }
interface FilterDef {
  key: string
  type: 'text' | 'select' | 'multiselect' | 'toggle' | 'daterange' | 'numberrange' | 'timerange' | 'selecttext'
  label: string
  placeholder?: string
  operatorLabel?: string
  options?: FilterOption[]
  primary?: boolean
  // multiselect only: quick presets ("Country: US (15)") that fill the selection with a set of
  // option values. `presetsLabel` names the dropdown button (default "Select by").
  presets?: Array<{ key: string; label: string; values: Array<string | number>; divided?: boolean }>
  presetsLabel?: string
  // multiselect only: render an "All" checkbox row above the list and give each `group` header a
  // tri-state checkbox that selects or clears every option under it. Both act on the visible
  // (filtered) options and add to the current selection rather than replacing it.
  selectAll?: boolean
  selectAllLabel?: string
  // numberrange only: show an "Unlimited" checkbox (value gets unlimited: true)
  allowUnlimited?: boolean
  // selecttext only: the option value that reveals the free-text input (e.g. a
  // "search by code" mode). Value shape is { mode, text }; text is only meaningful
  // when mode === textOption.
  textOption?: string | number
  // select only: the option value that reveals a companion date range inside this
  // popover (e.g. a "Custom range" period). `rangeKey` names the daterange def that
  // holds the dates; that def is normally `hidden` so it renders no chip of its own.
  rangeOption?: string | number
  rangeKey?: string
  // Participates in values (seeded, applied, cleared, summarized) but renders no chip.
  hidden?: boolean
}
interface DateRangeValue { mode: 'days' | 'range'; days: string; from: string; to: string }

const props = defineProps({
  // Filter schema (array of FilterDef).
  filters: { type: Array as () => FilterDef[], required: true },
  // Seed values keyed by filter key (read once on init).
  initialValue: { type: Object as () => Record<string, any>, default: () => ({}) },
  resultCount: { type: Number, default: null },
  perPage: { type: Number, default: null },
  perPageOptions: { type: Array as () => number[], default: () => [5, 10, 25, 50] },
  // Subtle refreshing indicator next to the result count (not a full-screen overlay).
  loading: { type: Boolean, default: false },
  // Chip-styled shortcut toggles rendered in the chip row (e.g. a "Today's bookings"
  // quick filter). Each: { key, label, active? }. Clicking emits 'quick-action' with the
  // key; the parent owns the active state and reflects it back via `active`.
  quickActions: { type: Array as () => Array<{ key: string; label: string; active?: boolean; end?: boolean; solid?: boolean }>, default: () => [] },
  // Opt-in: enables the saved-views chip and per-user filter persistence for this
  // page. The value is the stable view_key (see savedViewsRegistry). When set,
  // FilterBar becomes the source of the initial applied filter state and emits
  // exactly one initial `change` after resolving precedence — the page should let
  // that drive its first fetch rather than fetching in its own mounted hook.
  savedFiltersKey: { type: String, default: '' },
  // Opt-in: on phones the chip row collapses behind a filter button with an
  // active-filter count (the bookings mobile toolbar pattern). Pages that
  // already wrap the bar in their own mobile toolbar leave this off.
  mobileCollapsible: { type: Boolean, default: false },
  // Opaque page-owned state to store alongside the filters in a saved view (e.g.
  // the calendar's active-column selection/order). It is persisted under `_extra`
  // in the saved blob and handed back via the `restore-extra` event on apply; the
  // page restores it. FilterBar never interprets it. Deep-watched so changes
  // persist to the last-used view.
  extraSavedState: { type: Object, default: null },
  // Per-entity pages (one board per project, one results page per survey) pass the
  // entity the views belong to: { id, name? }. It is persisted under `_entity` in the
  // saved blob so the SavedViewsDrawer can open the right board, and presets saved for
  // another entity are hidden here. Rows with no `_entity` show for every entity.
  savedFiltersEntity: { type: Object as () => { id: string | number; name?: string } | null, default: null },
  // Opt-in sort chip: [{ key, label }] fields the page can sort by. The bar renders
  // the chip and popover; the page owns the value ({ key, dir }) and re-fetches on
  // update:sort. Persisted in saved views under _sort and restored via update:sort.
  sortOptions: { type: Array as () => Array<{ key: string; label: string }>, default: () => [] },
  sortValue: { type: Object as () => { key: string; dir: 'asc' | 'desc' } | null, default: null },
  // Pages whose table has sortable headers keep sort-options (so saved views still
  // carry _sort) and turn the chip off, so the sort control appears once.
  showSortChip: { type: Boolean, default: true },
  // Direction wording, e.g. { asc: 'A to Z', desc: 'Z to A' }; default ascending / descending.
  sortDirLabels: { type: Object as () => { asc: string; desc: string }, default: () => ({ asc: 'Ascending', desc: 'Descending' }) },
})

const emit = defineEmits<{
  (e: 'change', values: Record<string, any>): void
  (e: 'update:perPage', value: number): void
  (e: 'quick-action', key: string): void
  (e: 'open-saved-views'): void
  (e: 'restore-extra', extra: Record<string, any>): void
  (e: 'update:sort', value: { key: string; dir: 'asc' | 'desc' }): void
}>()

// Group settings drive the date-picker display only (presentational).
const group = JSON.parse(localStorage.getItem('group') || '{}')
// Element UI's date-picker validates firstDayOfWeek as 1–7 (Sunday = 7); 0 is invalid.
const firstDayOfWeek = group.first_day_of_week || 7
const dateDisplayFormat = computed(() => {
  let fmt = (group.date_format || 'MM-DD-YYYY').replaceAll('Y', 'y').replaceAll('D', 'd')
  return group.show_day_of_week ? 'ddd, ' + fmt : fmt
})
// Time-picker display follows the group's time format; emitted value-format stays 'HH:mm'.
const timeDisplayFormat = computed(() => (group.time_format === '12h' ? 'hh:mm A' : 'HH:mm'))
// Reformat a yyyy-MM-dd calendar date (wall-clock, no tz) into the group's date_format.
function formatGroupDate(iso: string): string {
  if (!iso) return ''
  const parts = iso.split('-')
  if (parts.length !== 3) return iso
  const [y, m, d] = parts
  return (group.date_format || 'YYYY-MM-DD').replace(/YYYY/g, y).replace(/MM/g, m).replace(/DD/g, d)
}

function emptyForType(def: FilterDef): any {
  if (def.type === 'toggle') return false
  if (def.type === 'daterange') return { mode: 'range', days: '', from: '', to: '' }
  if (def.type === 'numberrange') return def.allowUnlimited ? { from: '', to: '', unlimited: false } : { from: '', to: '' }
  if (def.type === 'timerange') return { from: '', to: '' }
  if (def.type === 'selecttext') return { mode: '', text: '' }
  if (def.type === 'multiselect') return []
  return ''
}
function isObjType(def: FilterDef): boolean {
  return def.type === 'daterange' || def.type === 'numberrange' || def.type === 'timerange' || def.type === 'selecttext'
}
function cloneVal(def: FilterDef, v: any): any {
  if (isObjType(def)) return { ...(v || emptyForType(def)) }
  if (def.type === 'multiselect') return Array.isArray(v) ? [...v] : []
  return v
}

const quickActionsBefore = computed(() => props.quickActions.filter(qa => !qa.end))
const quickActionsAfter = computed(() => props.quickActions.filter(qa => qa.end))

// Coerce an externally-provided value (URL seed or a saved view's stored value)
// into the internal shape for a filter def, filling gaps with the empty default.
function coerceProvided(def: FilterDef, provided: any): any {
  if (isObjType(def)) {
    const base = emptyForType(def)
    return provided ? { ...base, ...provided } : base
  }
  if (def.type === 'multiselect') return Array.isArray(provided) ? [...provided] : []
  // Toggles are strict booleans: saved views and URL params can hand back strings ('true', 'false')
  // or numbers, and the switch only shows on for true while the chip counted any truthy value.
  if (def.type === 'toggle') return provided === true || provided === 'true' || provided === 1 || provided === '1'
  return provided !== undefined && provided !== null ? provided : emptyForType(def)
}
function seedValue(def: FilterDef): any {
  return coerceProvided(def, props.initialValue ? props.initialValue[def.key] : undefined)
}

// Build the full objects FIRST, then make them reactive. Vue 2.7's reactive() (Object
// .defineProperty observer) can't track keys added after observation, so seeding onto an
// already-reactive {} would leave computeds like hasActiveFilters blind to later changes.
const committedInit: Record<string, any> = {}
const draftInit: Record<string, any> = {}
const msQueryInit: Record<string, string> = {}
for (const def of props.filters) {
  committedInit[def.key] = seedValue(def)
  draftInit[def.key] = cloneVal(def, committedInit[def.key])
  if (def.type === 'multiselect' || def.type === 'select') msQueryInit[def.key] = ''
}
// committed = applied values; draft = values edited inside a popover (committed on Apply).
const committed = reactive(committedInit)
const draft = reactive(draftInit)
// Per-filter option search text for multiselect and select (keys pre-seeded so Vue 2.7 tracks them).
const msQuery = reactive(msQueryInit)

// Filters that appear after mount (e.g. the UTM-gated source filter) — add reactively.
watch(() => props.filters.map(f => f.key).join('|'), () => {
  for (const def of props.filters) {
    if (!(def.key in committed)) set(committed, def.key, seedValue(def))
    if (!(def.key in draft)) set(draft, def.key, cloneVal(def, committed[def.key]))
    if ((def.type === 'multiselect' || def.type === 'select') && !(def.key in msQuery)) set(msQuery, def.key, '')
  }
})

const openKey = ref<string | null>(null)

// --- Sort chip ------------------------------------------------------------------
const sortOpen = ref(false)
const currentSort = computed<{ key: string; dir: 'asc' | 'desc' }>(() => {
  if (props.sortValue && props.sortValue.key) return { key: props.sortValue.key, dir: props.sortValue.dir === 'desc' ? 'desc' : 'asc' }
  const first = props.sortOptions[0]
  return { key: first ? first.key : '', dir: 'asc' }
})
const currentSortLabel = computed(() => {
  const opt = props.sortOptions.find(o => o.key === currentSort.value.key)
  return opt ? opt.label : ''
})
function setSort(key: string, dir: 'asc' | 'desc') {
  emit('update:sort', { key, dir })
}
function flipSort() {
  setSort(currentSort.value.key, currentSort.value.dir === 'asc' ? 'desc' : 'asc')
}
// A restored view carries _sort; hand it back to the page.
function restoreSort(values: Record<string, any> | null) {
  if (!values || props.sortOptions.length === 0) return
  const sv = values._sort
  if (sv && sv.key && props.sortOptions.some(o => o.key === sv.key)) {
    const dir: 'asc' | 'desc' = sv.dir === 'desc' ? 'desc' : 'asc'
    if (sv.key !== currentSort.value.key || dir !== currentSort.value.dir) emit('update:sort', { key: sv.key, dir })
  }
}
const moreOpen = ref(false)
// Secondary filters explicitly surfaced via "More filters" (shown even before active).
const revealed = ref<string[]>([])

const defByKey = computed(() => new Map(props.filters.map(f => [f.key, f])))
// Companion daterange def of a select whose rangeOption equals `v` (draft or committed).
function rangeDef(def: FilterDef, v: any): FilterDef | null {
  if (def.type !== 'select' || def.rangeOption === undefined || !def.rangeKey) return null
  if (v === '' || v == null || String(v) !== String(def.rangeOption)) return null
  const r = defByKey.value.get(def.rangeKey)
  return r && r.type === 'daterange' ? r : null
}
// Draft key whose date range body renders inside this popover, or '' for none.
function rangeDraftKey(def: FilterDef): string {
  if (def.type === 'daterange') return def.key
  const r = rangeDef(def, draft[def.key])
  return r && draft[r.key] ? r.key : ''
}
function dateRangeActive(v: any): boolean {
  if (!v) return false
  return v.mode === 'days' ? !!v.days : !!(v.from && v.to)
}
function dateRangeSummary(v: any): string {
  if (!v) return ''
  if (v.mode === 'days' && v.days) return 'Last ' + v.days + ' day' + (Number(v.days) === 1 ? '' : 's')
  if (v.mode === 'range' && v.from && v.to) return formatGroupDate(v.from) + ' – ' + formatGroupDate(v.to)
  return ''
}

function isActive(def: FilterDef): boolean {
  const v = committed[def.key]
  if (def.type === 'toggle') return !!v
  if (def.type === 'multiselect') return Array.isArray(v) && v.length > 0
  if (def.type === 'daterange') return dateRangeActive(v)
  if (def.type === 'select') {
    // A range-revealing option filters nothing until its dates are set.
    const r = rangeDef(def, v)
    if (r) return dateRangeActive(committed[r.key])
  }
  if (def.type === 'numberrange') {
    if (!v) return false
    if (def.allowUnlimited && v.unlimited) return true
    return v.from !== '' && v.from != null && v.to !== '' && v.to != null
  }
  if (def.type === 'timerange') return !!(v && v.from && v.to)
  if (def.type === 'selecttext') {
    if (!v || v.mode === '' || v.mode == null) return false
    // A text mode with no text entered filters nothing, so it's not "active".
    if (v.mode === def.textOption) return v.text !== '' && v.text != null
    return true
  }
  return v !== '' && v != null
}

const primaryFilters = computed(() => props.filters.filter(f => f.primary && !f.hidden))
const secondaryFilters = computed(() => props.filters.filter(f => !f.primary && !f.hidden))

// Chips that COULD be shown (primary + active/revealed secondary), before width capping.
const candidateChips = computed(() => [
  ...primaryFilters.value,
  ...secondaryFilters.value.filter(f => isActive(f) || revealed.value.includes(f.key)),
])

// Width cap so chips + per-page selector stay on one row; the rest fall into "More filters".
// maxVisibleChips is recomputed by measuring (see below); 9999 = no cap (initial / measuring).
const maxVisibleChips = ref(9999)
// Active filters (carry state) and filters the user explicitly revealed from "More filters" are
// never hidden by the width cap — only default (inactive, non-revealed) chips overflow.
const isPinned = (f: FilterDef) => isActive(f) || revealed.value.includes(f.key)
// Active and just-revealed chips stay visible so they can actually be used (setting
// a value happens on the chip); only inactive, non-revealed chips fold into "More
// filters". When the visible chips can't fit a narrow screen they wrap to a second
// line (see .filter-bar__chips) rather than overflowing the page.
const visibleChips = computed(() => {
  const cands = candidateChips.value
  if (maxVisibleChips.value >= cands.length) return cands
  let room = Math.max(0, maxVisibleChips.value - cands.filter(isPinned).length)
  const kept = new Set<string>()
  for (const f of cands) {
    if (isPinned(f)) { kept.add(f.key); continue }
    if (room > 0) { kept.add(f.key); room-- }
  }
  return cands.filter(f => kept.has(f.key))
})
const moreFilters = computed(() => {
  const visKeys = new Set(visibleChips.value.map(f => f.key))
  const overflow = candidateChips.value.filter(f => !visKeys.has(f.key))
  const neverShown = secondaryFilters.value.filter(f => !isActive(f) && !revealed.value.includes(f.key))
  return [...overflow, ...neverShown]
})
const hasActiveFilters = computed(() => props.filters.some(isActive))
const activeCount = computed(() => props.filters.filter(isActive).length)
const mobileOpen = ref(false)
function toggleMobile() {
  mobileOpen.value = !mobileOpen.value
  // The chip row was display:none while closed, so its widths were zero; measure again.
  if (mobileOpen.value) nextTick(() => scheduleRecompute())
}

// --- One-row overflow measurement ---------------------------------------------------------
// Render all candidate chips, measure them, then cap maxVisibleChips to how many fit beside the
// right cluster (results + actions + per-page). Keeps the bar on a single row at any width.
const rowEl = ref<HTMLElement | null>(null)
const chipsEl = ref<HTMLElement | null>(null)
const rightEl = ref<HTMLElement | null>(null)
function elWidth(el: HTMLElement | null): number {
  return el ? Math.ceil(el.getBoundingClientRect().width) : 0
}
let scheduled = false
function scheduleRecompute() {
  if (scheduled) return
  scheduled = true
  requestAnimationFrame(() => { scheduled = false; recomputeOverflow() })
}
async function recomputeOverflow() {
  maxVisibleChips.value = 9999
  await nextTick()
  const row = rowEl.value, chips = chipsEl.value
  if (!row || !chips) return
  const GAP = 8
  // Clamp to the viewport so an overflowing ancestor (e.g. a wide results table
  // that widens the page) can't inflate the measured width and defeat collapsing.
  const viewportW = typeof window !== 'undefined' ? window.innerWidth : row.clientWidth
  const avail = Math.min(row.clientWidth, viewportW) - elWidth(rightEl.value) - 12
  let reserved = 0
  chips.querySelectorAll<HTMLElement>('[data-fb-qa]').forEach(el => { reserved += elWidth(el) + GAP })
  const moreEl = chips.querySelector<HTMLElement>('[data-fb-more]')
  reserved += (moreEl ? elWidth(moreEl) : 132) + GAP // reserve for "More filters" even if not shown yet
  reserved += elWidth(chips.querySelector<HTMLElement>('[data-fb-clear]')) + GAP
  // Saved-views chip is always shown first and must never be overflowed.
  reserved += elWidth(chips.querySelector<HTMLElement>('[data-fb-views]')) + GAP
  // The sort chip (when a page opts in) is always shown too.
  reserved += elWidth(chips.querySelector<HTMLElement>('[data-fb-sort]')) + GAP
  let used = 0, count = 0
  for (const el of Array.from(chips.querySelectorAll<HTMLElement>('[data-fb-chip]'))) {
    const w = elWidth(el) + GAP
    if (used + w > avail - reserved) break
    used += w; count++
  }
  maxVisibleChips.value = count
}
let ro: ResizeObserver | null = null
onMounted(() => {
  scheduleRecompute()
  if (typeof ResizeObserver !== 'undefined' && rowEl.value) {
    ro = new ResizeObserver(() => scheduleRecompute())
    ro.observe(rowEl.value)
  }
})
onBeforeUnmount(() => { if (ro) { ro.disconnect(); ro = null } })
watch(
  () => [props.filters, committed, revealed.value, props.resultCount, props.loading, props.perPage],
  () => scheduleRecompute(),
  { deep: true }
)

function optionLabel(def: FilterDef, value: any): string {
  const opt = (def.options || []).find(o => o.value === value)
  return opt ? opt.label : String(value)
}
function valueSummary(def: FilterDef): string {
  const v = committed[def.key]
  if (def.type === 'select') {
    const r = rangeDef(def, v)
    return r ? dateRangeSummary(committed[r.key]) || optionLabel(def, v) : optionLabel(def, v)
  }
  if (def.type === 'toggle') return v ? 'On' : 'Off'
  if (def.type === 'multiselect') {
    const arr = Array.isArray(v) ? v : []
    return arr.map(val => optionLabel(def, val)).join(', ')
  }
  if (def.type === 'daterange') return dateRangeSummary(v)
  if (def.type === 'numberrange') {
    if (def.allowUnlimited && v.unlimited) return 'Unlimited'
    if (v.from !== '' && v.from != null && v.to !== '' && v.to != null) return v.from + ' – ' + v.to
    return ''
  }
  if (def.type === 'timerange') {
    if (v.from && v.to) return v.from + ' – ' + v.to
    return ''
  }
  if (def.type === 'selecttext') {
    if (v.mode === def.textOption) return v.text ? '"' + v.text + '"' : ''
    return optionLabel(def, v.mode)
  }
  return v
}

// ---- Multi-select helpers ----
function isChecked(def: FilterDef, val: any): boolean {
  const arr = draft[def.key]
  return Array.isArray(arr) && arr.includes(val)
}
// A preset replaces the draft selection with its values; Apply still commits it.
function applyOptionPreset(def: FilterDef, preset: { values: Array<string | number> }) {
  draft[def.key] = [...preset.values]
}
function toggleMulti(def: FilterDef, val: any) {
  const arr = Array.isArray(draft[def.key]) ? draft[def.key] : []
  draft[def.key] = arr.includes(val) ? arr.filter((x: any) => x !== val) : [...arr, val]
}
// Tri-state for a set of option values: 'on' when every value is in the draft, 'mixed' when
// some are, 'off' when none.
function bulkState(def: FilterDef, values: Array<string | number>): 'on' | 'mixed' | 'off' {
  if (values.length === 0) return 'off'
  const n = values.filter(v => isChecked(def, v)).length
  return n === values.length ? 'on' : n > 0 ? 'mixed' : 'off'
}
// Values under one header, restricted to what the search box currently shows.
function groupValues(def: FilterDef, group: string): Array<string | number> {
  return filteredOptions(def).filter(o => o.group === group).map(o => o.value)
}
// Header or "All" click: 'on' clears those values, anything else adds them (union, no duplicates).
function toggleBulk(def: FilterDef, values: Array<string | number>) {
  const arr: any[] = Array.isArray(draft[def.key]) ? draft[def.key] : []
  if (bulkState(def, values) === 'on') {
    draft[def.key] = arr.filter(x => !values.includes(x))
  } else {
    draft[def.key] = [...arr, ...values.filter(v => !arr.includes(v))]
  }
}
function filteredOptions(def: FilterDef): FilterOption[] {
  const q = (msQuery[def.key] || '').toLowerCase()
  const opts = def.options || []
  if (!q) return opts
  // A group heading counts as searchable text so typing a country lists its groups.
  return opts.filter(o => diacriticIncludes(o.label, q) || (o.group ? diacriticIncludes(o.group, q) : false))
}

function snapshot(): Record<string, any> {
  const out: Record<string, any> = {}
  for (const def of props.filters) out[def.key] = cloneVal(def, committed[def.key])
  return out
}
// What gets persisted in a saved view: the filter snapshot plus the page size
// (_perPage) when this bar owns pagination. Page size is restored on apply; page
// position is not (apply always lands on page 1). The `change` event still carries
// only the filter snapshot, so pages never see _perPage as a filter.
function savedSnapshot(): Record<string, any> {
  const snap = snapshot()
  if (props.perPage != null) snap._perPage = props.perPage
  if (props.sortOptions.length > 0) snap._sort = { ...currentSort.value }
  if (props.extraSavedState != null) snap._extra = props.extraSavedState
  const ent = props.savedFiltersEntity
  if (ent && ent.id != null && ent.id !== '') {
    snap._entity = ent.name ? { id: ent.id, name: ent.name } : { id: ent.id }
  }
  return snap
}
// Hand a restored view's page-owned extra state back to the page to apply.
function restoreExtra(values: Record<string, any> | null) {
  if (values && values._extra != null) emit('restore-extra', values._extra)
}
function emitChange() {
  // Any apply/remove/clear reflows the chip row, which would leave an open "More filters"
  // popover detached from its anchor — close it.
  moreOpen.value = false
  const snap = snapshot()
  // Persist the user's current filters (+ page size) as their per-user "last used".
  if (saved && savedReady.value) saved.saveLastUsed(savedSnapshot())
  emit('change', snap)
}

// --- Saved views (opt-in via savedFiltersKey) ------------------------------------------------
const saved = props.savedFiltersKey
  ? useSavedFilters(props.savedFiltersKey, () => (props.savedFiltersEntity ? props.savedFiltersEntity.id : null))
  : null
const viewsOpen = ref(false)
const savedReady = ref(false)
const savingPreset = ref(false)
const newViewName = ref('')
// The preset currently reflected on the page; cleared the moment the user edits a filter.
const appliedPresetId = ref<number | null>(null)
const savedPresets = computed<SavedFilterRow[]>(() => (saved ? saved.presets.value : []))
const currentViewLabel = computed(() => {
  if (appliedPresetId.value != null) {
    const p = savedPresets.value.find(x => x.id === appliedPresetId.value)
    if (p) return p.name
  }
  return 'Views'
})

const inst = getCurrentInstance()
// $route/$router are augmented onto the Vue prototype by vue-router 3 and aren't
// on the typed instance proxy, so reach them through an untyped view.
const vm = inst ? (inst.proxy as any) : null
function readViewParam(): number | null {
  const v = vm?.$route?.query?.view
  if (v == null || v === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}
function stripViewParam() {
  const router = vm?.$router
  const route = vm?.$route
  if (!router || !route) return
  const q = { ...route.query }
  if (!('view' in q)) return
  delete q.view
  router.replace({ query: q }).catch(() => {})
}

// Seed committed + draft from an external values object (a saved view). Mirrors the
// initial seeding but imperative, without emitting.
function applyValues(values: Record<string, any> | null) {
  for (const def of props.filters) {
    committed[def.key] = coerceProvided(def, values ? values[def.key] : undefined)
    draft[def.key] = cloneVal(def, committed[def.key])
  }
}

// Restore the page size stored in a view (if any), leaving position to reset.
function restorePerPage(values: Record<string, any> | null) {
  if (!values) return
  const pp = values._perPage
  if (pp != null && pp !== props.perPage) emit('update:perPage', pp)
}

function applyPreset(p: SavedFilterRow) {
  applyValues(p.filters)
  restorePerPage(p.filters)
  restoreSort(p.filters)
  restoreExtra(p.filters)
  appliedPresetId.value = p.id
  viewsOpen.value = false
  emitChange()
}

async function onSavePreset() {
  const name = newViewName.value.trim()
  if (!name || !saved) return
  savingPreset.value = true
  try {
    const id = await saved.savePreset(name, savedSnapshot())
    if (id != null) appliedPresetId.value = id
    newViewName.value = ''
    viewsOpen.value = false
  } finally {
    savingPreset.value = false
  }
}

async function toggleDefault(p: SavedFilterRow) {
  if (!saved) return
  await saved.setDefault(p.id, !p.is_default)
}

async function onDeletePreset(p: SavedFilterRow) {
  if (!saved) return
  await saved.remove(p.id)
  if (appliedPresetId.value === p.id) appliedPresetId.value = null
}

function onManageViews() {
  viewsOpen.value = false
  // Open the global drawer directly so pages don't each need to relay this;
  // the emit stays for any page that wants custom handling.
  openSavedViews()
  emit('open-saved-views')
}

// Apply a preset when the ?view= param appears/changes while already mounted
// (e.g. opening a view from the SavedViewsDrawer on the page you're already on).
function applyViewParam(id: number) {
  if (!saved) return
  const preset = saved.presets.value.find((p) => p.id === id)
  if (!preset) { stripViewParam(); return }
  applyValues(preset.filters)
  restorePerPage(preset.filters)
  restoreExtra(preset.filters)
  appliedPresetId.value = preset.id
  stripViewParam()
  emitChange()
}
watch(
  () => vm?.$route?.query?.view,
  (v) => {
    // Only react once the initial mount resolution has run (savedReady), so this
    // doesn't double-fire with onMounted's own param handling.
    if (!savedReady.value) return
    if (v == null || v === '') return
    const n = Number(v)
    if (Number.isFinite(n)) applyViewParam(n)
  },
)

// Resolve precedence on mount and drive the page's initial fetch. When saved views
// are enabled, FilterBar owns the initial applied state — the page must not fetch in
// its own mounted hook (it would race / double-fetch).
onMounted(async () => {
  if (!saved) return
  const presetId = readViewParam()
  await saved.load()
  // Re-seed committed from the CURRENT initial-value before resolving precedence.
  // A page may populate its filter values in its own mounted() hook, which runs
  // AFTER this child's setup — so the committed state seeded at setup can be stale
  // (missing URL filters). props.initialValue is reactive and reflects the page's
  // latest values by now (we just awaited saved.load()).
  applyValues(props.initialValue)
  const urlHasFilters = props.filters.some(isActive)
  const urlValues = snapshot()
  const res = saved.resolve({ presetId, urlValues, urlHasFilters })
  if (res.values) applyValues(res.values)
  restorePerPage(res.values)
  restoreSort(res.values)
  restoreExtra(res.values)
  appliedPresetId.value = res.appliedPresetId
  // Don't let restoring loaded last-used trigger a redundant save-back. Prime with the
  // stored view (which includes _perPage) so the perPage watcher below no-ops on restore.
  saved.primeLastUsedBaseline(res.values || savedSnapshot())
  if (presetId != null) stripViewParam()
  savedReady.value = true
  // One initial change so the page runs its first fetch with the resolved filters.
  emit('change', snapshot())
})

// Page-size changes (via the per-page selector) also persist to the last-used view.
watch(
  () => props.perPage,
  () => { if (saved && savedReady.value) saved.saveLastUsed(savedSnapshot()) },
)
// Sort changes persist to the last-used view too.
watch(
  () => props.sortValue,
  () => { if (saved && savedReady.value) saved.saveLastUsed(savedSnapshot()) },
  { deep: true },
)
// Page-owned extra state (e.g. active columns) changes also persist to last-used.
watch(
  () => props.extraSavedState,
  () => { if (saved && savedReady.value) saved.saveLastUsed(savedSnapshot()) },
  { deep: true },
)

function onPopoverToggle(def: FilterDef, visible: boolean) {
  // The draft persists across opens/closes — closing a popover without Apply keeps what was
  // typed, so values can be staged across several chips and applied together.
  if (visible) {
    openKey.value = def.key
  } else if (openKey.value === def.key) {
    openKey.value = null
  }
}

function applyFilter() {
  // Apply ALL staged drafts at once. Entering values in several chips then pressing Apply on
  // any one commits them together in a single request (mirrors the prior filter behavior).
  for (const def of props.filters) committed[def.key] = cloneVal(def, draft[def.key])
  appliedPresetId.value = null
  // Release focus from the Apply button (or any focused input) before the popover
  // closes; otherwise el-popover sets aria-hidden on an ancestor that still contains
  // the focused element, which the browser blocks as an a11y violation.
  if (typeof document !== 'undefined') (document.activeElement as HTMLElement | null)?.blur?.()
  emitChange()
  openKey.value = null
}

function removeFilter(def: FilterDef) {
  const r = rangeDef(def, committed[def.key])
  if (r) {
    committed[r.key] = emptyForType(r)
    draft[r.key] = emptyForType(r)
  }
  committed[def.key] = emptyForType(def)
  draft[def.key] = emptyForType(def)
  revealed.value = revealed.value.filter(k => k !== def.key)
  appliedPresetId.value = null
  openKey.value = null
  emitChange()
}

function onIconClick(def: FilterDef, e: Event) {
  // Active chip: the icon resets that filter and must NOT open the popover.
  if (isActive(def)) {
    e.stopPropagation()
    removeFilter(def)
  }
  // Inactive chip: let the click bubble so the popover opens.
}

function addFromMore(def: FilterDef) {
  if (!revealed.value.includes(def.key)) revealed.value = [...revealed.value, def.key]
  // Release focus from the clicked menu item before the popover closes, otherwise
  // el-popover sets aria-hidden on an ancestor that still contains the focused
  // element (blocked by the browser as an a11y violation).
  if (typeof document !== 'undefined') (document.activeElement as HTMLElement | null)?.blur?.()
  moreOpen.value = false
  nextTick(() => { openKey.value = def.key; onPopoverToggle(def, true) })
}

function clearAll() {
  for (const def of props.filters) {
    committed[def.key] = emptyForType(def)
    draft[def.key] = emptyForType(def)
  }
  revealed.value = []
  appliedPresetId.value = null
  openKey.value = null
  emitChange()
}

// Exposed so a parent can drive the bar imperatively (e.g. a page-level "Clear
// filters" button) instead of remounting via :key — remounting would re-run the
// saved-views precedence and re-apply the default/last-used, undoing the clear.
defineExpose({ clearAll, applyValues })
</script>

<script lang="ts">
export default { name: 'FilterBar' }
</script>

<style scoped>
.filter-bar__row {
  display: flex;
  /* Centered, not flex-start: the right group (count + actions slot +
     per-page select) is often taller than the chips, and flex-start aligns
     their top edges rather than their visual centers. */
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}
.filter-bar__chips {
  display: flex;
  align-items: center;
  /* Chips collapse into "More filters" to stay on one row (see measurement). Wrap
     is the last-resort safety net: when even the always-shown items (Views, More
     filters, Clear filters) can't fit a very narrow screen, they drop to a second
     line instead of overflowing the page. */
  flex-wrap: wrap;
  gap: 8px;
  row-gap: 8px;
  min-width: 0;
}
.filter-bar__right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  min-height: 30px;
}
/* Slotted action buttons carry the theme's default vertical margins, which
   would make the right group taller than it looks. */
.filter-bar__right ::v-deep .btn {
  margin-top: 0;
  margin-bottom: 0;
}
.filter-bar__count {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
}
.filter-bar__spinner {
  color: rgba(125, 125, 125, 0.8);
  font-size: 14px;
}
.filter-bar__perpage {
  max-width: 90px;
  margin-bottom: 0 !important;
}
/* Mobile collapse (opt-in via mobileCollapsible): the toggle only exists on phones;
   there the right cluster leads and the chip row drops below it when open. */
.filter-bar__toggle { display: none; }
@media (max-width: 767px) {
  .filter-bar--collapsible .filter-bar__row { flex-direction: column; align-items: stretch; }
  .filter-bar--collapsible .filter-bar__right { order: 1; justify-content: flex-start; gap: 12px; }
  .filter-bar--collapsible .filter-bar__count { margin-left: auto; }
  .filter-bar--collapsible .filter-bar__chips { order: 2; display: none; }
  .filter-bar--collapsible.filter-bar--open .filter-bar__chips { display: flex; }
  .filter-bar__toggle {
    position: relative;
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    order: -1;
    padding: 0;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 6px;
    background: transparent;
    color: rgba(255, 255, 255, 0.7);
    cursor: pointer;
  }
  .filter-bar__toggle.is-open { border-color: #e14eca; color: #e14eca; }
  .filter-bar__toggle-badge {
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    border-radius: 9px;
    background: #e14eca;
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    line-height: 18px;
    text-align: center;
  }
}

/* ---- Chips ---- */
.fb-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 30px;
  padding: 0 12px;
  font-size: 13px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.85);
  background: transparent;
  border: 1px dashed rgba(255, 255, 255, 0.25);
  border-radius: 16px;
  cursor: pointer;
  transition: border-color 0.15s ease, background 0.15s ease, color 0.15s ease;
  max-width: 380px;
}
.fb-chip:hover {
  border-color: rgba(255, 255, 255, 0.45);
  background: rgba(255, 255, 255, 0.04);
}
.fb-chip__icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.5);
  border-radius: 50%;
  transition: color 0.15s ease;
}
.fb-chip__icon:hover {
  color: #e14eca;
}
.fb-chip__label {
  font-weight: 600;
  white-space: nowrap;
}
.fb-chip__sep {
  color: rgba(255, 255, 255, 0.25);
}
.fb-chip__value {
  color: #e14eca;
  font-weight: 600;
  font-size: 12px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.fb-chip__chevron {
  font-size: 11px;
  color: #e14eca;
  flex-shrink: 0;
}
/* Solid-outline chip (opt-in via quick-action solid flag) */
.fb-chip--solid {
  border-style: solid;
}
/* Active chip: solid border, accent-tinted */
.fb-chip--active {
  border-style: solid;
  border-color: rgba(225, 78, 202, 0.4);
  background: rgba(225, 78, 202, 0.07);
}
.fb-chip--active:hover {
  border-color: rgba(225, 78, 202, 0.6);
  background: rgba(225, 78, 202, 0.12);
}

.fb-clear {
  background: none;
  border: none;
  padding: 0 6px;
  height: 30px;
  font-size: 13px;
  font-weight: 600;
  color: #e14eca;
  cursor: pointer;
  white-space: nowrap;
}
.fb-clear:hover {
  opacity: 0.8;
}
.fb-clear--disabled {
  opacity: 0.4;
  cursor: default;
}
.fb-clear--disabled:hover {
  opacity: 0.4;
}
</style>

<style lang="scss">
/* Popover content is teleported to body — must be unscoped. */
.fb-popover.el-popover {
  padding: 0;
  min-width: 260px;
  // Long option labels (free-text template names) wrap inside a capped panel instead of
  // stretching the popover across the viewport.
  max-width: 440px;
  background: #2a2a3d;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.5), 0 4px 12px rgba(0, 0, 0, 0.3);

  .popper__arrow { display: none; }
}
.fb-popover.fb-popover--menu { min-width: 200px; }

.fb-pop {
  padding: 14px;

  &__header {
    font-size: 14px;
    font-weight: 600;
    color: #fff;
    margin-bottom: 12px;
  }
  &__body { margin-bottom: 14px; }
  &__body--nested {
    margin-top: 12px;
    padding-top: 12px;
    border-top: 1px solid rgba(125, 125, 125, 0.3);
  }
  &__op {
    font-size: 12px;
    color: rgba(255, 255, 255, 0.55);
    margin-bottom: 6px;
  }
  &__toggle {
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-size: 13px;
    color: #fff;
  }
}

.fb-input {
  width: 100%;
  height: 36px;
  padding: 0 10px;
  font-size: 13px;
  color: #fff;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.18);
  border-radius: 6px;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus { border-color: #e14eca; }
  &::placeholder { color: rgba(255, 255, 255, 0.4); }
  &--days { width: 80px; }
}

.fb-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 7px 4px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 13px;
  color: #fff;

  &:hover { background: rgba(255, 255, 255, 0.06); }

  &__label {
    flex: 1;
    min-width: 0;
    white-space: normal;
    word-break: break-word;
    line-height: 1.35;
  }
  .badge-tag { flex-shrink: 0; }

  &__radio {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    border: 1.5px solid rgba(255, 255, 255, 0.35);
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: border-color 0.15s ease;

    &--on { border-color: #e14eca; }
  }
  &__dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #e14eca;
  }
}

/* selecttext: free-text input revealed under the mode radios */
.fb-selecttext__input { margin-top: 8px; }

.fb-radio {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #fff;
  cursor: pointer;
  margin-bottom: 0;

  input { accent-color: #e14eca; }
  &.mt-2 { margin-top: 12px; }
}
.fb-date-days-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 6px;
  padding-left: 24px;
}
.fb-date-suffix { font-size: 13px; color: rgba(255, 255, 255, 0.7); }
.fb-date-range-row {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 6px;
  padding-left: 24px;

  .el-date-editor { width: 100%; }

  // Dark-theme the date inputs so they match the rest of the popover (Element defaults to white).
  .el-date-editor .el-input__inner {
    height: 36px;
    line-height: 36px;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.18);
    border-radius: 6px;
    color: #fff;
    &::placeholder { color: rgba(255, 255, 255, 0.4); }
    &:focus { border-color: #e14eca; }
  }
  .el-date-editor .el-input__icon { color: rgba(255, 255, 255, 0.5); line-height: 36px; }
}

/* ---- Multi-select ---- */
.fb-ms-search { margin-bottom: 8px; }
.fb-ms-presets { margin-bottom: 8px; }
.fb-ms-list {
  max-height: 240px;
  overflow-y: auto;
}
.fb-ms-empty {
  font-size: 12px;
  color: rgba(255, 255, 255, 0.45);
  padding: 8px 4px;
}
.fb-ms-group {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.75);
  padding: 8px 4px 2px;
}
.fb-ms-group + .fb-option { margin-top: 0; }
.fb-check {
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1.5px solid rgba(255, 255, 255, 0.35);
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 0.15s ease, background 0.15s ease;

  i { font-size: 10px; color: #fff; }
  &--on { border-color: #e14eca; background: #e14eca; }
  &--mixed { border-color: #e14eca; }
  &--sm { width: 14px; height: 14px; i { font-size: 9px; } }
}
.fb-check__bar { width: 8px; height: 2px; border-radius: 1px; background: #e14eca; }
.fb-option--all { border-bottom: 1px solid rgba(255, 255, 255, 0.08); border-radius: 6px 6px 0 0; margin-bottom: 2px; }
.fb-ms-group--select {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  border-radius: 6px;
  &:hover { background: rgba(255, 255, 255, 0.06); }
}

/* ---- Number range ---- */
.fb-range-row {
  display: flex;
  align-items: center;
  gap: 8px;
}
.fb-range-dash { color: rgba(255, 255, 255, 0.5); }

.fb-apply {
  width: 100%;
  height: 38px;
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  background: #e14eca;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover { background: #d23bb8; }
}

.fb-more {
  padding: 6px;
  max-height: 320px;
  overflow-y: auto;

  &__item {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 8px 10px;
    font-size: 13px;
    color: #fff;
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    text-align: left;

    i { color: rgba(255, 255, 255, 0.5); font-size: 13px; }
    &:hover { background: rgba(255, 255, 255, 0.07); i { color: #e14eca; } }
  }
}

/* ---- Saved views ---- */
.fb-chip--views {
  .fb-chip__label { max-width: 140px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
}
.fb-views {
  padding: 14px 14px 12px;
  min-width: 240px;

  &__list {
    max-height: 240px;
    overflow-y: auto;
    margin-bottom: 12px;
  }
  &__item {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-radius: 6px;
    cursor: pointer;

    &:hover { background: rgba(255, 255, 255, 0.06); }
    &--active { background: rgba(225, 78, 202, 0.12); }
  }
  &__name {
    flex: 1;
    min-width: 0;
    font-size: 13px;
    color: #fff;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  &__star,
  &__del {
    flex-shrink: 0;
    width: 26px;
    height: 26px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: none;
    border: none;
    border-radius: 6px;
    cursor: pointer;
    color: rgba(255, 255, 255, 0.4);

    i { font-size: 13px; }
    &:hover { background: rgba(255, 255, 255, 0.1); color: #fff; }
  }
  &__star--on { color: #e14eca; }
  &__del:hover { color: #fd5d93; }

  &__save {
    display: flex;
    margin-bottom: 14px;

    // Joined input-group: input on the left, Save button attached on the right.
    .fb-input {
      border-radius: 6px 0 0 6px;
      border-right: none;
    }
  }
  &__save-btn {
    width: auto;
    flex-shrink: 0;
    padding: 0 14px;
    height: 36px;
    border-radius: 0 6px 6px 0;

    &:disabled { opacity: 0.5; cursor: not-allowed; }
  }
  &__manage {
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    padding: 12px 6px 2px;
    font-size: 13px;
    color: rgba(255, 255, 255, 0.75);
    background: none;
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 0;
    cursor: pointer;
    text-align: left;

    i { font-size: 14px; color: rgba(255, 255, 255, 0.5); }
    &:hover { color: #fff; i { color: #e14eca; } }
  }
}

/* ---- Light mode ---- */
.white-content {
  .filter-bar__count { color: rgba(0, 0, 0, 0.55); }
  .filter-bar__toggle { border-color: rgba(0, 0, 0, 0.2); color: rgba(0, 0, 0, 0.6); }
  .filter-bar__toggle.is-open { border-color: #e14eca; color: #e14eca; }

  .fb-views__name { color: #1e1e2f; }
  .fb-views__item:hover { background: rgba(0, 0, 0, 0.04); }
  .fb-views__item--active { background: rgba(225, 78, 202, 0.1); }
  .fb-views__star,
  .fb-views__del { color: rgba(0, 0, 0, 0.4); &:hover { background: rgba(0, 0, 0, 0.06); color: #1e1e2f; } }
  .fb-views__star--on { color: #e14eca; }
  .fb-views__del:hover { color: #fd5d93; }
  .fb-views__manage {
    color: rgba(0, 0, 0, 0.7);
    border-top-color: rgba(0, 0, 0, 0.1);
    i { color: rgba(0, 0, 0, 0.4); }
    &:hover { color: #1e1e2f; i { color: #e14eca; } }
  }

  .fb-chip {
    color: rgba(0, 0, 0, 0.8);
    border-color: rgba(0, 0, 0, 0.2);
  }
  .fb-chip:hover {
    border-color: rgba(0, 0, 0, 0.4);
    background: rgba(0, 0, 0, 0.03);
  }
  .fb-chip__icon { color: rgba(0, 0, 0, 0.4); }
  .fb-chip__sep { color: rgba(0, 0, 0, 0.2); }
  .fb-chip--active {
    border-color: rgba(225, 78, 202, 0.45);
    background: rgba(225, 78, 202, 0.07);
  }

  .fb-popover.el-popover {
    background: #fff;
    border-color: rgba(0, 0, 0, 0.12);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.15), 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  .fb-pop__header { color: #1e1e2f; }
  .fb-pop__op { color: rgba(0, 0, 0, 0.5); }
  .fb-pop__toggle { color: #1e1e2f; }
  .fb-input {
    color: #1e1e2f;
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(0, 0, 0, 0.15);
    &::placeholder { color: rgba(0, 0, 0, 0.4); }
  }
  .fb-option { color: #1e1e2f; &:hover { background: rgba(0, 0, 0, 0.04); } }
  .fb-option__radio { border-color: rgba(0, 0, 0, 0.3); }
  .fb-check { border-color: rgba(0, 0, 0, 0.3); &--on { border-color: #e14eca; background: #e14eca; i { color: #fff; } } }
  .fb-ms-empty { color: rgba(0, 0, 0, 0.45); }
  .fb-ms-group { color: rgba(0, 0, 0, 0.65); }
  .fb-option--all { border-bottom-color: rgba(0, 0, 0, 0.08); }
  .fb-ms-group--select:hover { background: rgba(0, 0, 0, 0.04); }
  .fb-range-dash { color: rgba(0, 0, 0, 0.5); }
  .fb-radio { color: #1e1e2f; }
  .fb-date-suffix { color: rgba(0, 0, 0, 0.6); }
  .fb-date-range-row .el-date-editor .el-input__inner {
    background: rgba(0, 0, 0, 0.03);
    border-color: rgba(0, 0, 0, 0.15);
    color: #1e1e2f;
    &::placeholder { color: rgba(0, 0, 0, 0.4); }
  }
  .fb-date-range-row .el-date-editor .el-input__icon { color: rgba(0, 0, 0, 0.4); }
  .fb-more__item {
    color: #1e1e2f;
    i { color: rgba(0, 0, 0, 0.4); }
    &:hover { background: rgba(0, 0, 0, 0.05); }
  }
}

/* Sort chip: the leading arrow flips direction on click without opening the popover */
.fb-sort__flip {
  display: inline-block;
  transition: transform 0.15s ease;
}
.fb-sort__flip--asc {
  transform: rotate(180deg);
}
.fb-sort__dir-label {
  margin-top: 10px;
}
</style>
